<script setup lang="ts">
import LazyImage from '~/components/design-system/lazy-image.vue';
import BannerOverlay from '~/components/marketing/banners/banner-overlay.vue';
import { useWinnersData } from '~/composables/useStates/useWinnersData';
import type { TWinnersFlags } from '~/interfaces/winners/winnersInterfaces';

definePageMeta({
  alias: '/winners',
});

useGetPage({ path: 'winners' });

const { $enabled } = useNuxtApp();
const siteStore = useSiteStore();
const auth = useAuthStore();
const { getLocale } = useLocaleUtils();
const { getPlaceholder, getDynamicImage } = useBranding();
const {
  loadBigWinners,
  loadMyBigWins,
  winnersLoading,
  myBigWins,
  bigWinners,
  currentWinners,
  hotGames,
  mostPopularGames,
  mostLikedGames,
} = useWinnersData();
const breakpoint = useBreakpoint();

const error = ref<string | null>(null);

const enableLoadBigWins = computed(
  () => $enabled('website.enablemybigwins') && !!auth.isLoggedIn,
);

onMounted(async () => {
  await loadBigWinners();
  if (enableLoadBigWins.value) await loadMyBigWins();
});

const imagePlaceholder = computed(() =>
  breakpoint.sm.value
    ? getPlaceholder('winnersPlaceholderMobile')
    : getPlaceholder('winnersPlaceholderDesktop'),
);

const fullWidth = computed(
  () => siteStore.bannerDevConfig?.enableFullWidth || false,
);

const image = computed(() =>
  getDynamicImage('bigWinners', {
    platform: breakpoint.sm.value ? 'mb' : 'dt',
    dimensions: breakpoint.sm.value ? '720_500' : '1600_300',
    locale: getLocale().split('-')[0],
    region: siteStore.getRegion,
  }),
);

const enableFlags = computed<TWinnersFlags>(() => ({
  myBigWins: enableLoadBigWins.value && !!myBigWins.value.length,
  bigWinners: !!bigWinners.value.length || !!winnersLoading.value,
  currentWinners: !!currentWinners.value.length || !!winnersLoading.value,
  categoryGames:
    !!hotGames.value.length ||
    !!mostLikedGames.value.length ||
    !!mostPopularGames.value.length ||
    !!winnersLoading.value,
}));

watch(
  () => winnersLoading.value,
  (newValue) => {
    Object.entries(enableFlags.value).some(([key]) => enableFlags.value[key]);
    if (
      !newValue &&
      !Object.entries(enableFlags.value).some(([key]) => enableFlags.value[key])
    )
      error.value = 'unable-to-retrieve-data';
  },
);

watch(
  () => auth.isLoggedIn,
  async (isLoggedIn) => {
    if ($enabled('website.enablemybigwins') && isLoggedIn)
      await loadMyBigWins();
  },
);
</script>

<template>
  <div>
    <banner-overlay>
      <LazyImage
        class="w-full h-full overflow-hidden relative object-cover"
        :class="[{ container: !fullWidth }]"
        :src="image"
        :placeholder="imagePlaceholder"
      />
    </banner-overlay>
    <div class="container py-2">
      <WinnersMyBigWins v-if="enableFlags.myBigWins" />
      <WinnersBigWinners v-if="enableFlags.bigWinners" />
      <WinnersBigWinnersTable v-if="enableFlags.currentWinners" />
      <WinnersCategory v-if="enableFlags.categoryGames" />
      <UserInterfaceGenericError
        v-if="error"
        state="danger"
        class="max-w-72 mx-auto mt-5"
      >
        <span v-html="$t(error)" />
      </UserInterfaceGenericError>
    </div>
  </div>
</template>
